import { useEffect, useRef } from "react";

/**
 * useReveal — robust scroll-reveal.
 * Strategy:
 *  - Sweep elements currently in viewport on mount + small ticks (handles
 *    React StrictMode double-mount & async-rendered subtrees)
 *  - IntersectionObserver for elements that scroll into view later
 *  - Throttled scroll listener fallback in case IO is suppressed by parent
 *    `visibility: hidden` ancestors (which our loader gate uses).
 */
export default function useReveal(
  selector: string = "[data-reveal]",
  rootMargin: string = "0px 0px -8% 0px"
) {
  const revealedRef = useRef<WeakSet<Element>>(new WeakSet());

  useEffect(() => {
    if (typeof window === "undefined") return;

    const reveal = (el: Element) => {
      if (revealedRef.current.has(el)) return;
      el.classList.add("is-revealed");
      revealedRef.current.add(el);
    };

    const sweep = () => {
      const els = document.querySelectorAll(selector);
      const vh = window.innerHeight;
      els.forEach((el) => {
        if (revealedRef.current.has(el)) return;
        const r = (el as HTMLElement).getBoundingClientRect();
        // Reveal if any part is within 95% of viewport (top or bottom edge)
        if (r.top < vh * 0.95 && r.bottom > vh * 0.05) {
          reveal(el);
        }
      });
    };

    // Initial passes
    sweep();
    const t1 = window.setTimeout(sweep, 80);
    const t2 = window.setTimeout(sweep, 400);
    const t3 = window.setTimeout(sweep, 1200);

    // IO for the rest
    let io: IntersectionObserver | null = null;
    try {
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              reveal(e.target);
              io && io.unobserve(e.target);
            }
          }
        },
        { threshold: 0.05, rootMargin }
      );
      document.querySelectorAll(selector).forEach((el) => {
        if (!revealedRef.current.has(el)) io!.observe(el);
      });
    } catch {
      /* IO not supported */
    }

    // Throttled scroll fallback
    let scrollTick = false;
    const onScroll = () => {
      if (scrollTick) return;
      scrollTick = true;
      window.requestAnimationFrame(() => {
        sweep();
        scrollTick = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      io && io.disconnect();
    };
  }, [selector, rootMargin]);
}
